import { Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { selectorFamily } from 'recoil';

import {
  advisorIdToApisSelector,
  branchIdToApisSelector,
  partnerIdToApisSelector,
} from '../master/selector';
import { sessionState } from '../session/atom';

import { tradingListItemDetailState } from './atom';
import {
  normalizeCustomerTradingList,
  normalizeTradingConsolidate,
  normalizeTradingCustomerPlatformsResponse,
  normalizeTradingCustomerProfileResponse,
  normalizeTradingCustomerTiesResponse,
} from './normalizer';
import {
  CustomerPlatformsItem,
  CustomerTiesItem,
  ObterIntradayResponse,
  ObterIntradayResponseNormalized,
  PlatformItem,
  TradingCustomerProfileItem,
} from './types';

export const tradingListCustomerQuery = selectorFamily<
  | {
      data: TradingCustomerProfileItem[];
    }
  | undefined,
  { searchTerm?: string }
>({
  key: 'TradingList/TradingList',
  get:
    ({ searchTerm }) =>
    async ({ get }) => {
      const branchSelected = get(branchIdToApisSelector);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);
      const { userProfile } = get(sessionState);

      const tradingCustomerListResponse = await (searchTerm?.length
        ? Service.External.Extranet.Movimentacao.V1.movimentacaoObterFiltro({
            idAssessor: advisorSelected,
            idFilialIntranet: branchSelected,
            idParceiro: partnerSelected ?? userProfile?.idParceiro ?? 0,
            sinacor: parseInt(searchTerm, 10),
            screenName: 'trading-customer-list',
          })
        : Service.External.Extranet.Movimentacao.V1.movimentacaoObterTrading({
            idAssessor: advisorSelected,
            idFilialIntranet: branchSelected,
            idParceiro: partnerSelected ?? userProfile?.idParceiro ?? 0,
            screenName: 'trading-customer-list',
          }));

      const data = normalizeCustomerTradingList(tradingCustomerListResponse);

      return {
        data,
      };
    },
});

export const tradingListDetailsQuery = selectorFamily<
  | {
      data: ObterIntradayResponseNormalized[];
    }
  | undefined,
  { idConta: number; sinacor: number; idContaSuperPNP: number }
>({
  key: 'TradingList/TradingListDetails',
  get:
    ({ idConta, sinacor, idContaSuperPNP }) =>
    async () => {
      const intradayListResponse =
        await Service.External.Extranet.Movimentacao.V1.movimentacaoObterIntraDay({
          sinacor,
          idConta,
          idContaSuperPNP,
          screenName: 'trading-customer-list',
        });

      if (Array.isArray(intradayListResponse) && intradayListResponse.length > 0) {
        const typedResponse: ObterIntradayResponse = intradayListResponse;

        const intradayListNormalized: ObterIntradayResponseNormalized[] = typedResponse
          .sort((a, b) => b.id - a.id)
          .map((item) => {
            return {
              id: item.id,
              tradingsConsolidate: normalizeTradingConsolidate(item.tradingDataConsolidadoDTO),
            };
          });

        return {
          data: intradayListNormalized,
        };
      } else {
        return undefined;
      }
    },
});

export const tradingCustomerHeroDetailsQuery = selectorFamily<
  | {
      data: TradingCustomerProfileItem;
    }
  | undefined,
  { idConta: number }
>({
  key: 'TradingList/TradingCustomerHeroDetails',
  get:
    ({ idConta }) =>
    async () => {
      const response =
        await Service.External.Extranet.Movimentacao.V1.movimentacaoObterInformacoesClienteTrading({
          idConta,
          screenName: 'TradingList',
        });

      const data = normalizeTradingCustomerProfileResponse(response);

      return {
        data,
      };
    },
});

export const tradingCustomerTiesQuery = selectorFamily<
  | {
      data: CustomerTiesItem[];
    }
  | undefined,
  { idConta: number }
>({
  key: 'TradingList/TradingCustomerTies',
  get:
    ({ idConta }) =>
    async () => {
      const response =
        await Service.External.Extranet.Movimentacao.V1.movimentacaoObterVinculoTrading({
          idConta,
          screenName: 'TradingList',
        });

      const data = normalizeTradingCustomerTiesResponse(response);

      return {
        data,
      };
    },
});

export const tradingCustomerPlatformsQuery = selectorFamily<
  | {
      data: CustomerPlatformsItem[];
    }
  | undefined,
  { idConta: number }
>({
  key: 'TradingList/TradingCustomerPlatforms',
  get:
    ({ idConta }) =>
    async () => {
      const response =
        await Service.External.Extranet.Movimentacao.V1.movimentacaoObterPlataformasTrading({
          idConta,
          screenName: 'TradingList',
        });

      const data = normalizeTradingCustomerPlatformsResponse(response);

      return {
        data,
      };
    },
});

export const tradingListItemDetailSingleState = selectorFamily<
  PlatformItem,
  { idConta: number; sinacor: number; idContaSuperPNP: number; id: number }
>({
  key: 'TradingList/tradingListItemDetailSingleState',
  get:
    ({ id, idConta, idContaSuperPNP, sinacor }) =>
    ({ get }) => {
      const tradingList = get(tradingListItemDetailState({ idConta, sinacor, idContaSuperPNP }));

      if (tradingList.length > 0) {
        const found = tradingList.find((item) => item.id === id);

        if (found) {
          return {
            id: found.id,
            platformName: found.name,
            balance: Utils.Mask.Money(found.financeiroDisponivelAlocacao),
            allocatedLimit: Utils.Mask.Money(found.limiteAlocado),
            acceptableLoss: Utils.Mask.Money(found.prejuizoMaximoAceitavel),
            resultDay: Utils.Mask.Money(found.resultadoDia),
          };
        }
      }

      return {
        id,
        platformName: '--',
        resultDay: '--',
        acceptableLoss: '--',
        allocatedLimit: '--',
        balance: '--',
      };
    },
});
